import primaryComponents, {
  BackButton,
} from "../../../../components/primaryComponents";
import secondaryComponents from "../../../../components/secondaryComponents";
import styles from "./InvoiceDetails.module.scss";
import { extractReadableDateOnly, formatCurrency } from "../../../../helpers";
import { motion } from "framer-motion";
import { useInvoiceDetails } from "./useInvoiceDetails";
import { useSelector } from "react-redux";
import globalStyles from "../../../../assets/styles/base/globalPage.module.scss";
import { Link } from "react-router-dom";
import { pathConstants } from "../../../../routes/pathContants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useReactToPrint } from "react-to-print";
import { useRef } from "react";

const InvoiceDetails = () => {
  const { isFetchingInvoiceDetails, isFetchingSupplier, invoiceDetails } =
    useInvoiceDetails();
  const { currency_code } = useSelector(
    (state) => state.profileDetailsReducer.businessDetails,
  );

  const printRef = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Invoice_${invoiceDetails?.invoice_number || invoiceDetails?.id || "details"}`,
  });

  return (
    <secondaryComponents.MetaSetter
      title="Lumi Merchant | Invoice Details"
      description="View invoice details"
    >
      <secondaryComponents.RequestLoaderTrigger>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={`${globalStyles.pageColumnGaps} ${styles.invoiceDetails}`}
        >
          <div>
            <BackButton />
          </div>

          <secondaryComponents.LoaderHelper
            classNames={"mt-5"}
            isLoading={isFetchingInvoiceDetails || isFetchingSupplier}
          >
            <div ref={printRef} className={styles.printableInvoice}>
              <div className={`${globalStyles.pageHeaders} mb-3`}>
                <div>
                  <h3 className="font-weight-normal">
                    <span className="gray--text">Invoice Number: </span>
                    {invoiceDetails?.id || "N/A"}
                  </h3>
                </div>
                <div className="ml-auto no-print">
                  <primaryComponents.Button
                    classNames="btn btn--outline"
                    onClick={handlePrint}
                  >
                    <FontAwesomeIcon icon="print" className="mr-2" />
                    Print Invoice
                  </primaryComponents.Button>
                </div>
              </div>

              <div className={globalStyles.gridDetailsView}>
                <div>
                  <p className="dark--text">Suppier</p>

                  <Link
                    to={pathConstants.INVENTORY_SUPPLIERS_DETAILS({
                      id: invoiceDetails?.supplier?.id,
                    })}
                    className="purple--text small-text-size"
                  >
                    {invoiceDetails?.supplier?.name || "N/A"}
                  </Link>
                </div>
                <div>
                  <p className="dark--text">Invoice Number</p>
                  <p className="small-text-size">
                    {invoiceDetails?.invoice_number || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="dark--text">Invoice Amount</p>
                  <p className="small-text-size">
                    {formatCurrency({
                      value: invoiceDetails?.total,
                      currencyCode: currency_code,
                    })}
                  </p>
                </div>

                <div>
                  <p className="dark--text">Amount Paid</p>
                  <p className="small-text-size">
                    {formatCurrency({
                      value: invoiceDetails?.amount_paid,
                      currencyCode: currency_code,
                    })}
                  </p>
                </div>

                <div>
                  <p className="dark--text">Outstanding Amount</p>
                  <div
                    className="d-flex align-items-center"
                    style={{ gap: "10px" }}
                  >
                    <p
                      className={`small-text-size ${invoiceDetails?.outstanding_balance > 0 ? "error--text" : "success--text"}`}
                    >
                      {formatCurrency({
                        value: invoiceDetails?.outstanding_balance,
                        currencyCode: currency_code,
                      })}
                    </p>
                  </div>
                </div>

                <div>
                  <p className="dark--text">PO Number</p>
                  <Link
                    to={pathConstants.INVENTORY_PURCHASE_ORDER_DETAILS({
                      id: invoiceDetails?.purchase_order?.id,
                    })}
                    className="small-text-size purple--text cursor-pointer"
                  >
                    {invoiceDetails?.purchase_order?.po_number || "N/A"}
                  </Link>
                </div>
                <div>
                  <p className="dark--text">GRN Number</p>
                  {invoiceDetails?.goods_received?.length > 0 ? (
                    <>
                      {invoiceDetails?.goods_received?.map((item) => (
                        <>
                          <Link
                            key={item?.id}
                            to={pathConstants.INVENTORY_GOODS_RECEIVED_DETAILS({
                              id: item?.id,
                            })}
                            className="small-text-size purple--text"
                          >
                            {item?.gr_number || "N/A"}
                          </Link>
                          {", "}
                        </>
                      ))}
                    </>
                  ) : (
                    <p className="small-text-size">N/A</p>
                  )}
                </div>

                <div>
                  <p className="dark--text">Bill Number</p>
                  {invoiceDetails?.goods_received?.length > 0 ? (
                    <>
                      {invoiceDetails?.bills?.map((item) => (
                        <>
                          <Link
                            key={item?.id}
                            to={pathConstants.INVENTORY_BILLS_DETAILS({
                              id: item?.id,
                            })}
                            className="small-text-size purple--text"
                          >
                            {item?.bill_number || "N/A"}
                          </Link>
                          {", "}
                        </>
                      ))}
                    </>
                  ) : (
                    <p className="small-text-size">N/A</p>
                  )}
                </div>
                <div>
                  <p className="dark--text">Payment Method</p>
                  <p className="small-text-size">
                    {invoiceDetails?.payment_method || "N/A"}
                  </p>
                </div>

                <div>
                  <p className="dark--text">Invoice Date</p>
                  <p className="small-text-size">
                    {extractReadableDateOnly(invoiceDetails?.invoice_date) ||
                      "N/A"}
                  </p>
                </div>
                <div>
                  <p className="dark--text">Date</p>
                  <p className="small-text-size">
                    {extractReadableDateOnly(invoiceDetails?.created_at) ||
                      "N/A"}
                  </p>
                </div>
                <div>
                  <p className="dark--text">Invoice Due Date</p>
                  <p className="small-text-size">
                    {extractReadableDateOnly(
                      invoiceDetails?.invoice_due_date,
                    ) || "N/A"}
                  </p>
                </div>
                {invoiceDetails?.invoice_url && (
                  <div>
                    <p className="dark--text">Invoice Image</p>
                    <p
                      onClick={() => {
                        window.open(invoiceDetails?.invoice_url, "_blank");
                      }}
                      className="small-text-size purple--text"
                    >
                      Click to view invoice image
                    </p>
                  </div>
                )}
              </div>
            </div>
          </secondaryComponents.LoaderHelper>
        </motion.div>
      </secondaryComponents.RequestLoaderTrigger>
    </secondaryComponents.MetaSetter>
  );
};

export default InvoiceDetails;
